import { useEffect, useState } from 'react';
import { useRef } from 'react';
import React from 'react';

import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';

import { AddFundsToWallet } from './Steps/AddFundsToWallet';
import { Create } from './Steps/Create';
import { useMonitorContext } from '../MonitorContextProvider';

type CreateWalletProps = {
  open: boolean;
  onClose: () => void;
  onFinish: () => void;
};

export function CreateWallet({ open, onClose, onFinish }: CreateWalletProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [preventClose, setPreventClose] = useState(false);
  const { setWalletDraft, handleSetFunds, setDocument, setCsvValidation } =
    useMonitorContext();
  const createRef = useRef<{ validateAndSubmit: () => Promise<boolean> }>(null);

  const handleClose = () => {
    if (preventClose) {
      return;
    }

    setActiveStep(0);
    setWalletDraft({
      name: '',
      description: '',
      startDate: null,
      funds: [],
    });
    handleSetFunds([]);
    setDocument(null);
    setCsvValidation(null);
    onClose();
  };

  const handlePreventClose = (prevent: boolean) => {
    setPreventClose(prevent);
  };

  const handleNext = async () => {
    if (await createRef.current?.validateAndSubmit()) {
      setActiveStep(1);
    }
  };

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setDocument(null);
      setCsvValidation(null);
    }
  }, [open, setDocument, setCsvValidation]);

  const handleFinishAndReset = () => {
    setActiveStep(0);
    onFinish();
  };

  return (
    <Dialog
      open={open}
      onClose={preventClose ? undefined : handleClose}
      fullWidth
      maxWidth="lg"
      disableEscapeKeyDown={preventClose}>
      <DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={preventClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: preventClose
              ? theme.palette.action.disabled
              : theme.palette.grey[500],
          })}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper
            activeStep={activeStep}
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              paddingBottom: 1,
              zIndex: 1000,
            }}>
            <Step
              sx={{ cursor: preventClose ? 'default' : 'pointer' }}
              onClick={preventClose ? undefined : () => setActiveStep(0)}>
              <StepLabel>Criar carteira</StepLabel>
            </Step>
            <Step
              sx={{ cursor: preventClose ? 'default' : 'pointer' }}
              onClick={preventClose ? undefined : handleNext}>
              <StepLabel>Adicionar fundos</StepLabel>
            </Step>
          </Stepper>
          <Box sx={{ p: 2 }}>
            {activeStep === 0 && (
              <Create
                ref={createRef}
                onCancel={handleClose}
                onFinish={handleNext}
              />
            )}
            {activeStep === 1 && (
              <AddFundsToWallet
                onCancel={() => setActiveStep(0)}
                onFinish={handleFinishAndReset}
                onPreventClose={handlePreventClose}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
